import { roundingOff } from '../../../../Components/helper';
import type { CostingErrorField, MultiSubmitRow } from './types';

export interface CostingRequestDto {
  productInfo: {
    supplierId: string;
    currency: string;
    incoterms: string;
    shipmentMethod: string;
    reference: string;
    targetCost: string;
    factoryId: string;
    moqNeeded: string;
    sampleRef: string;
    costingTemplate: string;
  };
  coreProduct: {
    colourways: string[];
    sizes: string[];
    lengths: string[];
    quantity: string;
    pricePerUnit: string;
    totalPrice: string;
  };
  /** When true, Submit asks the costing Lambda to send the supplier invite after creating the costing. */
  invite: boolean;
  /** Legacy field retained on the stored record. The Lambda now emails the invited supplier's own
   *  email, resolved server-side from the supplier id — this Compose "To" value no longer sets the recipient. */
  inviteEmail: string;
}

export interface CostingBuildContext {
  /** Maps a Costing-Information supplier id to its currency code (supplierList.companies.currency). */
  supplierCurrencyById: Record<string, string>;
}

/** Row id -> the required fields still empty on that row. Rows with no gaps are absent. */
export type CostingErrorMap = Record<string, CostingErrorField[]>;

export interface CostingErrorResult {
  errors: CostingErrorMap;
  /** How many products have at least one gap — drives the summary toast count. */
  productCount: number;
  /** Product number of the first row whose supplier resolves to no currency. Only set when
   *  no field gaps remain, so the user is never shown an error they cannot act on. */
  currencyMissingFor?: string;
}

/** A Costing Information supplier is the switch that says "this product wants a costing". */
export function hasCosting(row: MultiSubmitRow): boolean {
  return !!row.ciSupplier;
}

/** A required value counts as present only once trimmed. `0` is a filled number. */
function isFilled(value: string | undefined): boolean {
  return (value ?? '').trim() !== '';
}

/**
 * Collect every missing required costing field across the grid. Rows without a Costing
 * Information supplier want no costing and are skipped entirely, so a grid may freely mix
 * costed and uncosted products.
 */
export function collectCostingErrors(
  rows: MultiSubmitRow[],
  supplierCurrencyById: Record<string, string>,
): CostingErrorResult {
  const errors: CostingErrorMap = {};
  let currencyMissingFor: string | undefined;

  for (const r of rows) {
    if (!hasCosting(r)) continue;

    const core = r.coreLines[0];
    const missing: CostingErrorField[] = [];
    if (!isFilled(r.ciCostingTemplate)) missing.push('ciCostingTemplate');
    if (!isFilled(r.ciIncoterms)) missing.push('ciIncoterms');
    if (!isFilled(r.ciShipmentMethod)) missing.push('ciShipmentMethod');
    if (!isFilled(core?.quantity)) missing.push('core.quantity');

    if (missing.length > 0) errors[r.id] = missing;
    if (!currencyMissingFor && !isFilled(supplierCurrencyById[r.ciSupplier])) {
      currencyMissingFor = r.productNumber;
    }
  }

  const productCount = Object.keys(errors).length;
  /* Field gaps win: they are actionable on the cell, the currency problem is not. */
  return {
    errors,
    productCount,
    ...(productCount === 0 && currencyMissingFor ? { currencyMissingFor } : {}),
  };
}

/** Total for the single core line: quantity x pricePerUnit, 2dp — same helper the Total cell renders with. */
function computeTotal(quantity: string, pricePerUnit: string): string {
  return roundingOff(Number(quantity || 0) * Number(pricePerUnit || 0), 2);
}

/** Build the costing request carried on one product's submit-download request. */
export function buildCostingRequest(row: MultiSubmitRow, ctx: CostingBuildContext): CostingRequestDto {
  const core = row.coreLines[0];
  return {
    productInfo: {
      supplierId: row.ciSupplier,
      currency: ctx.supplierCurrencyById[row.ciSupplier] ?? '',
      incoterms: row.ciIncoterms,
      shipmentMethod: row.ciShipmentMethod,
      reference: row.ciReference,
      targetCost: row.ciTargetCost,
      factoryId: row.ciFactory,
      moqNeeded: row.ciMoqNeeded,
      sampleRef: row.ciSampleRef,
      costingTemplate: row.ciCostingTemplate,
    },
    coreProduct: {
      colourways: core?.colourway ?? [],
      sizes: core?.size ?? [],
      lengths: core?.length ?? [],
      quantity: core?.quantity ?? '',
      pricePerUnit: core?.pricePerUnit ?? '',
      totalPrice: computeTotal(core?.quantity ?? '', core?.pricePerUnit ?? ''),
    },
    invite: !!row.invite,
    inviteEmail: row.emailTo ?? '',
  };
}
